import React, { useState } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { Task, TaskStatus, Column, SortConfig } from '../types';
import { TaskCard } from './TaskCard';

interface TaskColumnProps {
  column: Column;
  tasks: Task[];
  count: number;
  draggedTaskId: string | null;
  sortConfig: SortConfig;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onDrop: (status: TaskStatus) => void;
  onCardClick: (task: Task) => void;
  onAddTask: () => void;
  onSortChange: (config: SortConfig) => void;
  wide?: boolean;
}

const SORT_OPTIONS: { label: string; field: SortConfig['field']; direction: SortConfig['direction'] }[] = [
  { label: 'Newest first', field: 'createdAt', direction: 'desc' },
  { label: 'Oldest first', field: 'createdAt', direction: 'asc' },
  { label: 'Due date', field: 'dueDate', direction: 'asc' },
  { label: 'Priority', field: 'priority', direction: 'asc' },
  { label: 'Title A–Z', field: 'title', direction: 'asc' },
];

const COLUMN_HEADER_STYLES: Record<TaskStatus, string> = {
  backlog: 'border-t-gray-400',
  todo: 'border-t-blue-500',
  in_progress: 'border-t-amber-500',
  done: 'border-t-emerald-500',
};

const COLUMN_COUNT_STYLES: Record<TaskStatus, string> = {
  backlog: 'bg-gray-100 text-gray-600',
  todo: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
};

export const TaskColumn: React.FC<TaskColumnProps> = ({
  column,
  tasks,
  count,
  draggedTaskId,
  sortConfig,
  onDragStart,
  onDragEnd,
  onDrop,
  onCardClick,
  onAddTask,
  onSortChange,
  wide = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSort, setShowSort] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop(column.id);
  };

  const currentSortLabel =
    SORT_OPTIONS.find(o => o.field === sortConfig.field && o.direction === sortConfig.direction)
      ?.label ?? 'Sort';

  return (
    <div className={`flex flex-col ${wide ? 'w-full' : 'w-full md:min-w-[280px] md:max-w-[320px] md:flex-1'}`}>
      {/* Column header */}
      <div
        className={`bg-white rounded-t-lg border border-b-0 border-gray-200 border-t-2 px-3 py-3 ${COLUMN_HEADER_STYLES[column.id]}`}
      >
        <div className='flex items-center justify-between mb-2'>
          <div className='flex items-center gap-2'>
            <span className={`text-sm font-semibold ${column.color}`}>{column.title}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${COLUMN_COUNT_STYLES[column.id]}`}>
              {count}
            </span>
            {wide && (
              <span className='hidden sm:inline text-[11px] text-gray-400 ml-1'>
                — deprioritized tasks waiting to be picked up
              </span>
            )}
          </div>
          <button
            onClick={onAddTask}
            className='p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors'
            title='Add task'
          >
            <Plus size={14} />
          </button>
        </div>

        {/* Sort control */}
        <div className='relative'>
          <button
            onClick={() => setShowSort(v => !v)}
            className='flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors'
          >
            <ChevronDown size={11} />
            {currentSortLabel}
          </button>
          {showSort && (
            <div className='absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]'>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={`${opt.field}-${opt.direction}`}
                  onClick={() => {
                    onSortChange({ field: opt.field, direction: opt.direction });
                    setShowSort(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                    sortConfig.field === opt.field && sortConfig.direction === opt.direction
                      ? 'text-blue-600 font-semibold'
                      : 'text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border border-t-0 border-gray-200 rounded-b-lg p-2 transition-colors duration-150
          ${wide ? 'min-h-[120px]' : 'flex-1 min-h-[300px] md:min-h-[400px]'}
          ${isDragOver ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}
        `}
      >
        <div className='space-y-2'>
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onCardClick}
              isDragging={draggedTaskId === task.id}
              compact={wide}
            />
          ))}
        </div>

        {tasks.length === 0 && !isDragOver && (
          <div className='flex flex-col items-center justify-center h-16 text-gray-300'>
            <p className='text-xs'>No tasks in backlog</p>
          </div>
        )}

        {isDragOver && (
          <div className='border-2 border-dashed border-blue-300 rounded-lg h-16 flex items-center justify-center mt-2'>
            <p className='text-xs text-blue-500 font-medium'>Drop here to deprioritize</p>
          </div>
        )}
      </div>
    </div>
  );
};
